/**
 * Work Shift API Service
 * Handles work shift CRUD operations with backend
 */

import { apiGet, apiPost, apiPut, apiDelete } from '@/shared/api/apiClient';
import type { PagedResult } from '@/types';

/**
 * Work Shift DTOs matching backend models.
 * `type` (numeric enum) + `typeLabel` were replaced by the generic
 * common.system_codes-backed pair `typeId` (system code RowId, GUID) +
 * `typeValue` (human-readable label) — see useSystemCodes('ShiftType').
 */
export interface WorkShiftDto {
  id: string;
  tenantId: string;
  name: string;
  code: string;
  typeId: string;
  typeValue: string;
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
  gracePeriodMinutes: number;
  workHoursPerDay: number;
  breakDurationMinutes: number;
  workingDays: string; // e.g., "1,2,3,4,5"
  isNightShift: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface WorkShiftListDto {
  id: string;
  name: string;
  code: string;
  typeId: string;
  typeValue: string;
  startTime: string;
  endTime: string;
  isNightShift: boolean;
  isActive: boolean;
}

export interface CreateWorkShiftRequest {
  name: string;
  code: string;
  typeId: string;
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
  gracePeriodMinutes?: number;
  workHoursPerDay?: number;
  breakDurationMinutes?: number;
  workingDays?: string;
  isNightShift?: boolean;
  isActive?: boolean;
  tenantId: string;
}

export interface UpdateWorkShiftRequest {
  name: string;
  code: string;
  typeId: string;
  startTime: string;
  endTime: string;
  gracePeriodMinutes?: number;
  workHoursPerDay?: number;
  breakDurationMinutes?: number;
  workingDays?: string;
  isNightShift?: boolean;
  isActive?: boolean;
  tenantId: string;
}

/**
 * Get all work shifts for a tenant
 */
export async function getAllWorkShifts(
  tenantId: string,
  isActive?: boolean
): Promise<WorkShiftListDto[]> {
  const params = new URLSearchParams({ tenantId });
  if (isActive !== undefined) {
    params.append('isActive', isActive.toString());
  }
  return apiGet<WorkShiftListDto[]>(`/work-shifts?${params.toString()}`);
}

export interface WorkShiftListParams {
  tenantId: string;
  /** 0-based row offset. */
  offset?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}

export interface WorkShiftListResult {
  items: WorkShiftListDto[];
  totalCount: number;
}

/**
 * Server-side paginated/searchable variant of `getAllWorkShifts`, used only by
 * the Configuration screen's table. `getAllWorkShifts` above is left untouched —
 * it still serves dropdown selects elsewhere unpaginated.
 */
export async function getWorkShiftsPaged(params: WorkShiftListParams): Promise<WorkShiftListResult> {
  const query = new URLSearchParams({ tenantId: params.tenantId });
  if (params.offset !== undefined) query.append('offset', String(params.offset));
  if (params.limit !== undefined) query.append('limit', String(params.limit));
  if (params.search) query.append('search', params.search);
  if (params.isActive !== undefined) query.append('isActive', String(params.isActive));

  const data = await apiGet<PagedResult<WorkShiftListDto>>(`/work-shifts?${query.toString()}`);
  return { items: data.items ?? [], totalCount: data.totalCount ?? 0 };
}

/**
 * Get a work shift by ID
 */
export async function getWorkShiftById(
  id: string,
  tenantId: string
): Promise<WorkShiftDto> {
  return apiGet<WorkShiftDto>(`/work-shifts/${id}?tenantId=${tenantId}`);
}

/**
 * Create a new work shift
 */
export async function createWorkShift(
  data: CreateWorkShiftRequest
): Promise<{ id: string }> {
  return apiPost<{ id: string }>('/work-shifts', data);
}

/**
 * Update an existing work shift
 */
export async function updateWorkShift(
  id: string,
  data: UpdateWorkShiftRequest
): Promise<void> {
  return apiPut<void>(`/work-shifts/${id}`, data);
}

/**
 * Delete a work shift (soft delete - sets IsActive to false)
 */
export async function deleteWorkShift(id: string, tenantId: string): Promise<void> {
  return apiDelete<void>(`/work-shifts/${id}`, { tenantId });
}
